import { useTranslation } from "react-i18next";

import { Group } from "@dashboard/devices/types";
import { hasEffectivePolicy } from "@dashboard/devices/utils/license";
import { toWholePercent } from "@dashboard/groups/utils";
import { getStatus, StatusIcon } from "@dashboard/shared/components/StatusIcon";
import { Box, Typography } from "@mui/material";
import { LoadingIcon } from "@shared/Loading";

type Props = {
    group: Group;
    /**
     * The hash of the policy the page shows. Until the groups reload that follows an assignment or a change of the
     * rules, the stored group still carries another hash and its statistics belong to that one.
     */
    policyHash: string | null;
};

export const ComplianceCell = ({ group, policyHash }: Props) => {
    const { t } = useTranslation(["pages", "common"]);

    const getContent = () => {
        const own = group.statistics?.own;
        if (!own || group.policyHash !== policyHash) {
            return <LoadingIcon />;
        }
        if (own.deviceCount === 0) {
            return <StatusIcon status="none" tooltip={t("common:tooltip.groupIsEmpty")} />;
        }
        if (!hasEffectivePolicy(group)) {
            return <StatusIcon status="none" tooltip={t("common:tooltip.policyHasNoRules")} />;
        }
        const { compliant } = own.policyStatus;
        return (
            <>
                <StatusIcon
                    status={getStatus(compliant, own.deviceCount)}
                    tooltip={t("pages:policiesPage.tooltip.compliantDevices", { count: compliant, total: own.deviceCount })}
                />
                <Typography variant="body1" component="span">
                    {`${toWholePercent(compliant, own.deviceCount)}%`}
                </Typography>
            </>
        );
    };

    // Every state sits in the same flex row, so the icon lines up at the start of the cell in each of them.
    return <Box sx={{ display: "flex", alignItems: "center" }}>{getContent()}</Box>;
};
